import * as Sentry from '@sentry/browser';
import { TaskCm } from '../data/models';
import {
  summarizeTask,
  TaskPositionSnapshot,
} from '../planning-grid/planning-diagnostics/planning-diagnostics.types';

/** W3C baggage keys (short; `wwag.` avoids colliding with Sentry DSC). */
export const PLANNING_BAGGAGE = {
  task: 'wwag.task',
  workPackage: 'wwag.wp',
  project: 'wwag.prj',
  employeeBefore: 'wwag.emp.b',
  employeeAfter: 'wwag.emp.a',
  fromBefore: 'wwag.from.b',
  fromAfter: 'wwag.from.a',
  toBefore: 'wwag.to.b',
  toAfter: 'wwag.to.a',
} as const;

const SENTRY_TAG = {
  taskId: 'task.id',
  workPackage: 'task.wp',
  projectId: 'task.project',
  employeeBefore: 'emp.before',
  employeeAfter: 'emp.after',
  fromBefore: 'from.before',
  fromAfter: 'from.after',
  toBefore: 'to.before',
  toAfter: 'to.after',
} as const;

export interface PlanningEntityTrace {
  taskId?: number;
  workPackage?: number;
  projectId?: number;
  employeeBefore?: string;
  employeeAfter?: string;
  fromBefore?: string;
  fromAfter?: string;
  toBefore?: string;
  toAfter?: string;
}

const stack: PlanningEntityTrace[] = [];
let lastTrace: PlanningEntityTrace | undefined;

export function planningEntityTraceFromTasks(
  before: TaskCm | TaskPositionSnapshot | null | undefined,
  after: TaskCm | TaskPositionSnapshot | null | undefined,
): PlanningEntityTrace {
  const beforeSnap = isTaskCm(before) ? summarizeTask(before) : before ?? null;
  const afterSnap = isTaskCm(after) ? summarizeTask(after) : after ?? null;
  const afterTask = isTaskCm(after) ? after : undefined;
  const beforeTask = isTaskCm(before) ? before : undefined;
  return {
    taskId: afterSnap?.id ?? beforeSnap?.id,
    workPackage: afterTask?.identifier ?? beforeTask?.identifier,
    projectId: afterSnap?.projectId ?? beforeSnap?.projectId,
    employeeBefore: beforeSnap?.employeeId,
    employeeAfter: afterSnap?.employeeId,
    fromBefore: beforeSnap?.from,
    fromAfter: afterSnap?.from,
    toBefore: beforeSnap?.to,
    toAfter: afterSnap?.to,
  };
}

/**
 * Request-scoped mutation context for W3C baggage. Sentry tags stay on the
 * isolation scope so later events (errors, jump report) still show the last move.
 */
export function pushPlanningEntityTrace(trace: PlanningEntityTrace): () => void {
  stack.push(trace);
  lastTrace = trace;
  applyPlanningEntityTraceToSentry(trace);
  return () => {
    const index = stack.lastIndexOf(trace);
    if (index >= 0) {
      stack.splice(index, 1);
    }
  };
}

export function currentPlanningEntityTrace(): PlanningEntityTrace | undefined {
  return stack[stack.length - 1];
}

/** Last reschedule mutation, including after the HTTP call finished. */
export function lastPlanningEntityTrace(): PlanningEntityTrace | undefined {
  return currentPlanningEntityTrace() ?? lastTrace;
}

export function planningEntityTraceForRequest(
  body: unknown,
): PlanningEntityTrace | undefined {
  const taskId = taskIdFromBody(body);
  if (taskId != null) {
    for (let i = stack.length - 1; i >= 0; i--) {
      if (stack[i].taskId === taskId) {
        return stack[i];
      }
    }
  }
  return currentPlanningEntityTrace();
}

export function planningEntityTraceToBaggage(
  trace: PlanningEntityTrace,
): Record<string, string> {
  const members: Record<string, string> = {};
  setMember(members, PLANNING_BAGGAGE.task, trace.taskId);
  setMember(members, PLANNING_BAGGAGE.workPackage, trace.workPackage);
  setMember(members, PLANNING_BAGGAGE.project, trace.projectId);
  setMember(members, PLANNING_BAGGAGE.employeeBefore, trace.employeeBefore);
  setMember(members, PLANNING_BAGGAGE.employeeAfter, trace.employeeAfter);
  setMember(members, PLANNING_BAGGAGE.fromBefore, trace.fromBefore);
  setMember(members, PLANNING_BAGGAGE.fromAfter, trace.fromAfter);
  setMember(members, PLANNING_BAGGAGE.toBefore, trace.toBefore);
  setMember(members, PLANNING_BAGGAGE.toAfter, trace.toAfter);
  return members;
}

export function applyPlanningEntityTraceToSentry(trace: PlanningEntityTrace): void {
  lastTrace = trace;
  const tags: Record<string, string> = {};
  setTag(tags, SENTRY_TAG.taskId, trace.taskId);
  setTag(tags, SENTRY_TAG.workPackage, trace.workPackage);
  setTag(tags, SENTRY_TAG.projectId, trace.projectId);
  setTag(tags, SENTRY_TAG.employeeBefore, trace.employeeBefore);
  setTag(tags, SENTRY_TAG.employeeAfter, trace.employeeAfter);
  setTag(tags, SENTRY_TAG.fromBefore, trace.fromBefore);
  setTag(tags, SENTRY_TAG.fromAfter, trace.fromAfter);
  setTag(tags, SENTRY_TAG.toBefore, trace.toBefore);
  setTag(tags, SENTRY_TAG.toAfter, trace.toAfter);
  try {
    if (Object.keys(tags).length > 0) {
      Sentry.setTags(tags);
    }
    const span = Sentry.getActiveSpan();
    if (span) {
      span.setAttributes(tags);
    }
  } catch {
    // Sentry not initialized
  }
}

export function mergeBaggageHeader(
  existing: string | undefined,
  members: Record<string, string>,
): string | undefined {
  const map = parseBaggage(existing);
  for (const [key, value] of Object.entries(members)) {
    if (value) {
      map.set(key, value);
    }
  }
  if (map.size === 0) {
    return undefined;
  }
  return [...map.entries()]
    .map(([key, value]) => `${key}=${encodeBaggageValue(value)}`)
    .join(',');
}

export function parseBaggage(header: string | undefined): Map<string, string> {
  const map = new Map<string, string>();
  if (!header) {
    return map;
  }
  for (const part of header.split(',')) {
    const idx = part.indexOf('=');
    if (idx <= 0) {
      continue;
    }
    const key = part.slice(0, idx).trim();
    const raw = part.slice(idx + 1).trim();
    if (!key) {
      continue;
    }
    try {
      map.set(key, decodeURIComponent(raw));
    } catch {
      map.set(key, raw);
    }
  }
  return map;
}

export function resetPlanningEntityTraceForTests(): void {
  stack.length = 0;
  lastTrace = undefined;
}

function encodeBaggageValue(value: string): string {
  return encodeURIComponent(value).replace(/%20/g, '+');
}

function setMember(
  members: Record<string, string>,
  key: string,
  value: string | number | undefined,
): void {
  if (value == null || value === '') {
    return;
  }
  members[key] = String(value);
}

function setTag(
  tags: Record<string, string>,
  key: string,
  value: string | number | undefined,
): void {
  if (value == null || value === '') {
    return;
  }
  tags[key] = String(value).slice(0, 200);
}

function taskIdFromBody(body: unknown): number | undefined {
  if (body && typeof body === 'object' && !Array.isArray(body) && 'id' in body) {
    const id = (body as { id?: unknown }).id;
    return typeof id === 'number' ? id : undefined;
  }
  return undefined;
}

function isTaskCm(
  value: TaskCm | TaskPositionSnapshot | null | undefined,
): value is TaskCm {
  return !!value && 'identifier' in value && 'changeToken' in value;
}
